import type { IntlShape, MessageDescriptor } from 'react-intl';

import { renderHighlightedText } from '@evisort/eds/utils/renderHighlightedText';
import { defineMessages, useIntl } from 'react-intl';

import { EM_DASH } from '~/common/constants/unicode';

import { ReadComponentProps, UfFieldValue } from '../questionInputRegistry';
import { joinReadValues } from '../ufValueFormatters';
import { ReadChipDisplay } from './ReadChipDisplay';
import { ReadTextDisplay } from './ReadTextDisplay';

const messages = defineMessages({
  day: {
    id: 'DurationReadComponent.period.day',
    defaultMessage: '{count, plural, one {# day} other {# days}}',
    description: 'Duration period display: day(s)',
  },
  week: {
    id: 'DurationReadComponent.period.week',
    defaultMessage: '{count, plural, one {# week} other {# weeks}}',
    description: 'Duration period display: week(s)',
  },
  month: {
    id: 'DurationReadComponent.period.month',
    defaultMessage: '{count, plural, one {# month} other {# months}}',
    description: 'Duration period display: month(s)',
  },
  quarter: {
    id: 'DurationReadComponent.period.quarter',
    defaultMessage: '{count, plural, one {# quarter} other {# quarters}}',
    description: 'Duration period display: quarter(s)',
  },
  year: {
    id: 'DurationReadComponent.period.year',
    defaultMessage: '{count, plural, one {# year} other {# years}}',
    description: 'Duration period display: year(s)',
  },
});

/** Plain-text formatting shared with `UfField`'s copy/DataRecord text, which
 * (unlike this component) can't render the JSX read variants directly. */
export const formatDuration = (
  intl: IntlShape,
  value: UfFieldValue | undefined,
): string | null => {
  const amount = value?.amount;
  const period = value?.period;
  if (amount == null || period == null) return null;
  const message = (messages as Partial<Record<string, MessageDescriptor>>)[
    period
  ];
  const count = Number(amount);
  return message
    ? intl.formatMessage(message, { count })
    : `${amount} ${period}`;
};

export const DurationReadComponent = ({
  values,
  variant,
  search,
}: ReadComponentProps) => {
  const intl = useIntl();
  const durations = values
    .map((v) => formatDuration(intl, v))
    .filter((formatted): formatted is string => Boolean(formatted));

  if (variant === 'chip') {
    return (
      <ReadChipDisplay
        items={durations.length ? durations.map((text) => ({ text })) : null}
      />
    );
  }

  if (variant === 'text') {
    return (
      <ReadTextDisplay
        text={durations.length ? joinReadValues(durations) : null}
      />
    );
  }

  if (!durations.length) return <>{EM_DASH}</>;
  return <>{renderHighlightedText(joinReadValues(durations), search)}</>;
};
